import { validateWorkspaceSearch } from "@/features/workspace-router";
import { useTakeoverBack } from "@/lib/takeoverOrigin";
import { SurfaceHeader, SurfaceLayout, cn } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Plug } from "lucide-react";

/**
 * ``/integrations`` route group — fullscreen takeover (June 10, 2026
 * promoted from ``/workspace/integrations``).
 *
 * Why fullscreen: the connector hub renders a dense grid of capability
 * cards + per-capability wizard pages (Salesforce / Fatture-in-Cloud
 * setup forms, Drive folder picker tree, Slack channel scope picker
 * …). The workspace split-shell (38% chat + 62% workspace) crops the
 * forms below the readability threshold — user feedback "fammela
 * aprire a tutto schermo come le impostazioni". Same chrome strip as
 * Settings, Brand Kits, etc (every other admin-like surface in
 * ``_authed/_modal``).
 *
 * Old URL ``/workspace/integrations[/...]`` is preserved as a
 * permanent redirect (``_authed/workspace/integrations/route.tsx``)
 * so bookmarks + in-flight WS messages keep working.
 *
 * BACK AFFORDANCE (Aug 25, 2026): like ``_modal/settings/route.tsx``, the
 * hub paints its OWN in-flow back bar above the ``SurfaceLayout`` — the
 * ModalShell's bar never showed up here for the user, leaving the takeover
 * with no way out but ESC. ``_modal/route.tsx`` skips its own bar for
 * ``/integrations/**`` so there is exactly ONE.
 */
export const Route = createFileRoute("/_authed/_modal/integrations")({
  validateSearch: validateWorkspaceSearch,
  component: IntegrationsShell,
});

function IntegrationsShell() {
  const { i18n } = useLingui();
  // Back = the workspace surface the user actually came from (Documenti, chat,
  // an action proposal). Tracked in ``lib/takeoverOrigin`` rather than popped
  // off the history: the drawer navigates with ``replace``, so there is no
  // usable entry behind us.
  const back = useTakeoverBack();

  return (
    <div className="flex h-full flex-col bg-cream">
      {/* Back bar — deliberately light: a ghost control, no pill, no
          divider, optically aligned with the "Configurazione" label below
          (``-ml-2`` cancels the button's own padding). It is a plain in-flow
          element, not a ``fixed`` overlay, so no stacking context can hide it
          — that was the bug this replaced. */}
      <div className="shrink-0 pt-3">
        <div className="mx-auto w-full max-w-6xl px-8">
          <button
            type="button"
            onClick={back}
            className={cn(
              "group -ml-2 inline-flex h-8 items-center gap-1.5 rounded-lg px-2 font-medium text-[12px] text-ink-2 transition-colors hover:text-ink-1",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/70 focus-visible:ring-offset-1 focus-visible:ring-offset-cream",
            )}
            aria-label={i18n._(msg`Torna al workspace`)}
          >
            <ArrowLeft
              size={14}
              strokeWidth={2}
              aria-hidden
              className="group-hover:-translate-x-0.5 text-ink-3 transition-transform duration-150 group-hover:text-ink-1 motion-reduce:transform-none"
            />
            <span>{i18n._(msg`Torna al workspace`)}</span>
          </button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        <SurfaceLayout
          header={
            <SurfaceHeader
              className="mx-auto w-full max-w-6xl"
              label={i18n._(msg`Configurazione`)}
              title={i18n._(msg`Integrazioni`)}
              meta={
                <span className="inline-flex items-center gap-1 text-ink-3">
                  <Plug size={12} aria-hidden />{" "}
                  <Trans>connetti le sorgenti che alimentano Ando</Trans>
                </span>
              }
            />
          }
          contentClassName="px-0 pt-0"
          contentAriaLabel={i18n._(msg`Lista integrazioni e wizard di configurazione`)}
        >
          <Outlet />
        </SurfaceLayout>
      </div>
    </div>
  );
}
